import { AlertCircle, LoaderCircle, X } from "lucide-react";
import {
  Component,
  type ErrorInfo,
  type ReactNode,
  useEffect
} from "react";

interface DeferredChunkErrorBoundaryProps {
  children: ReactNode;
  fallback: ReactNode;
}

interface DeferredChunkErrorBoundaryState {
  failed: boolean;
}

export class DeferredChunkErrorBoundary extends Component<
  DeferredChunkErrorBoundaryProps,
  DeferredChunkErrorBoundaryState
> {
  state: DeferredChunkErrorBoundaryState = { failed: false };

  static getDerivedStateFromError(): DeferredChunkErrorBoundaryState {
    return { failed: true };
  }

  componentDidCatch(error: unknown, info: ErrorInfo) {
    console.error("Deferred model details failed to load", error, info.componentStack);
  }

  render() {
    return this.state.failed ? this.props.fallback : this.props.children;
  }
}

interface DeferredDetailOverlayProps {
  closeLabel: string;
  description: string;
  onClose: () => void;
  title: string;
}

/*
 * The panel reads top to bottom as icon, heading, description and then any
 * action, and its close control is the dialog's own: the same label, cross,
 * size and corner inset, so nothing moves when the dialog replaces it.
 */

function useEscapeToClose(onClose: () => void) {
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      onClose();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);
}

function DeferredDetailOverlay({
  actions,
  closeLabel,
  description,
  icon,
  onClose,
  title,
  ...aria
}: DeferredDetailOverlayProps &
  { actions?: ReactNode; icon: ReactNode } &
  Pick<React.AriaAttributes, "aria-busy" | "aria-live">) {
  useEscapeToClose(onClose);

  return (
    <div className="deferred-detail-layer">
      <div
        className="detail-scrim"
        role="presentation"
        onClick={(event) => {
          if (event.target === event.currentTarget) onClose();
        }}
      />
      <section
        className="deferred-detail-panel"
        data-lenis-prevent
        role="dialog"
        aria-modal="true"
        aria-labelledby="deferred-detail-title"
        aria-describedby="deferred-detail-description"
        {...aria}
      >
        <button
          type="button"
          className="detail-close deferred-detail-panel__close"
          aria-label={closeLabel}
          onClick={onClose}
        >
          <span>Esc</span>
          <X size={15} aria-hidden="true" />
        </button>
        <div className="deferred-detail-panel__content">
          {icon}
          <h2 id="deferred-detail-title">{title}</h2>
          <p id="deferred-detail-description">{description}</p>
          {actions}
        </div>
      </section>
    </div>
  );
}

interface DeferredDetailLoadingProps {
  closeLabel: string;
  description: string;
  onClose: () => void;
  title: string;
}

export function DeferredDetailLoading(props: DeferredDetailLoadingProps) {
  return (
    <DeferredDetailOverlay
      {...props}
      aria-busy="true"
      aria-live="polite"
      icon={
        <LoaderCircle
          className="spin deferred-detail-panel__icon"
          size={25}
          aria-hidden="true"
        />
      }
    />
  );
}

interface DeferredDetailFailureProps {
  closeLabel: string;
  description: string;
  onClose: () => void;
  onReload: () => void;
  reloadLabel: string;
  title: string;
}

export function DeferredDetailFailure({
  onReload,
  reloadLabel,
  ...overlayProps
}: DeferredDetailFailureProps) {
  return (
    <DeferredDetailOverlay
      {...overlayProps}
      aria-live="assertive"
      icon={
        <AlertCircle
          className="deferred-detail-panel__icon"
          size={25}
          aria-hidden="true"
        />
      }
      actions={
        <button type="button" className="primary-button" onClick={onReload}>
          <span>{reloadLabel}</span>
        </button>
      }
    />
  );
}
